import { useState, useRef, useEffect } from 'react';
import {
  CalendarIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  ChevronDownIcon
} from '@heroicons/react/24/outline';

/**
 * @typedef {Object} DateRangePickerProps
 * @property {string} label - Label for the date range picker
 * @property {string} startDate - Selected start date value
 * @property {string} endDate - Selected end date value
 * @property {(startDate: string, endDate: string) => void} onChange - Function called when dates change
 * @property {string} [maxDate] - Optional maximum date allowed (in YYYY-MM-DD format)
 * @property {string} [minDate] - Optional minimum date allowed (in YYYY-MM-DD format)
 */

const DAYS = ['S', 'M', 'T', 'W', 'T', 'F', 'S'];
const MONTHS = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December'
];

const PRESETS = [
  { label: 'Today', type: 'today' },
  { label: 'Yesterday', type: 'yesterday' },
  { label: 'This month', type: 'thisMonth' },
  { label: 'Last month', type: 'lastMonth' }
];

/**
 * Get calendar days for a given month and year
 * @param {number} month - Month (0-11)
 * @param {number} year - Year
 * @returns {Array<{ date: Date, isCurrentMonth: boolean }>} Calendar days
 */
function getCalendarDays(month, year) {
  const firstDay = new Date(Date.UTC(year, month, 1));
  const lastDay = new Date(Date.UTC(year, month + 1, 0));
  const days = [];

  // Add days from previous month
  for (let i = firstDay.getUTCDay(); i > 0; i--) {
    const date = new Date(Date.UTC(year, month, 1 - i));
    days.push({ date, isCurrentMonth: false });
  }

  // Add days from current month
  for (let i = 1; i <= lastDay.getUTCDate(); i++) {
    const date = new Date(Date.UTC(year, month, i));
    days.push({ date, isCurrentMonth: true });
  }

  // Add days from next month
  const remainingDays = 42 - days.length; // 6 rows * 7 days = 42
  for (let i = 1; i <= remainingDays; i++) {
    const date = new Date(Date.UTC(year, month + 1, i));
    days.push({ date, isCurrentMonth: false });
  }

  return days;
}

/**
 * Format date as YYYY-MM-DD
 * @param {Date} date - Date to format
 * @returns {string} Formatted date
 */
function formatDate(date) {
  const year = date.getUTCFullYear();
  const month = String(date.getUTCMonth() + 1).padStart(2, '0');
  const day = String(date.getUTCDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

/**
 * Parse YYYY-MM-DD string to Date
 * @param {string} dateString - Date string to parse
 * @returns {Date} Parsed date
 */
function parseDate(dateString) {
  if (!dateString || typeof dateString !== 'string') return new Date();

  try {
    const [year, month, day] = dateString.split('-').map(Number);
    if (!year || !month || !day) return new Date();
    const date = new Date(Date.UTC(year, month - 1, day));
    // Validate if the date is valid and not NaN
    if (isNaN(date.getTime())) return new Date();
    return date;
  } catch (error) {
    return new Date();
  }
}

/**
 * Calculate date range based on preset
 * @param {Object} preset - The preset configuration
 * @returns {{ fromDate: string, toDate: string }} The calculated date range
 */
function calculatePresetRange(preset) {
  const today = new Date();
  const todayStr = formatDate(today);

  if (preset.type === 'today') {
    return { fromDate: todayStr, toDate: todayStr };
  }

  if (preset.type === 'yesterday') {
    const yesterday = new Date(today);
    yesterday.setUTCDate(today.getUTCDate() - 1);
    const yesterdayStr = formatDate(yesterday);
    return { fromDate: yesterdayStr, toDate: yesterdayStr };
  }

  if (preset.days) {
    const fromDateObj = new Date(today);
    fromDateObj.setUTCDate(today.getUTCDate() - preset.days);
    return { fromDate: formatDate(fromDateObj), toDate: todayStr };
  }

  if (preset.type === 'thisMonth') {
    const fromDate = formatDate(
      new Date(Date.UTC(today.getUTCFullYear(), today.getUTCMonth(), 1))
    );
    return { fromDate, toDate: todayStr };
  }

  if (preset.type === 'lastMonth') {
    const firstDayLastMonth = new Date(
      Date.UTC(today.getUTCFullYear(), today.getUTCMonth() - 1, 1)
    );
    const lastDayLastMonth = new Date(
      Date.UTC(today.getUTCFullYear(), today.getUTCMonth(), 0)
    );
    return {
      fromDate: formatDate(firstDayLastMonth),
      toDate: formatDate(lastDayLastMonth)
    };
  }

  return { fromDate: '', toDate: '' };
}

const formatPanelDate = (date) =>
  new Date(date).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: '2-digit',
    timeZone: 'UTC'
  });

/**
 * Custom date range picker component with DaisyUI styling
 * @param {DateRangePickerProps} props - Component props
 * @returns {JSX.Element} Rendered date range picker
 */
export default function DateRangePicker(props) {
  const { label, startDate, endDate, onChange, maxDate, minDate } = props;

  const [isOpen, setIsOpen] = useState(false);
  const [viewDate, setViewDate] = useState(() => new Date());
  const [hoverDate, setHoverDate] = useState(null);
  const [selectingStart, setSelectingStart] = useState(true);
  const [localStartDate, setLocalStartDate] = useState(startDate);
  const [localEndDate, setLocalEndDate] = useState(endDate);
  const selectedStartRef = useRef(localStartDate);
  const dropdownRef = useRef(null);

  // Update local state when props change
  useEffect(() => {
    setLocalStartDate(startDate);
    setLocalEndDate(endDate);
    selectedStartRef.current = startDate;
  }, [startDate, endDate]);

  const selectedStartDate = localStartDate ? parseDate(localStartDate) : null;
  const selectedEndDate = localEndDate ? parseDate(localEndDate) : null;
  const maxDateObj = maxDate ? parseDate(maxDate) : null;
  const minDateObj = minDate ? parseDate(minDate) : new Date('2025-01-01');

  //Extract Previous month for left panel
  const prevMonthDate = new Date(
    viewDate.getFullYear(),
    viewDate.getMonth() - 1
  );
  const leftCalendarDays = getCalendarDays(
    prevMonthDate.getMonth(),
    prevMonthDate.getFullYear()
  );

  // Reset selection state when opening the picker
  useEffect(() => {
    if (isOpen) {
      setSelectingStart(true);
    }
  }, [isOpen]);

  useEffect(() => {
    function handleClickOutside(event) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    }

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const calendarDays = getCalendarDays(
    viewDate.getMonth(),
    viewDate.getFullYear()
  );

  function handleDateSelect(date) {
    const selectedDate = new Date(date);
    selectedDate.setHours(0, 0, 0, 0);

    // Check max and min date constraints
    if (maxDate) {
      const maxDateObj = parseDate(maxDate);
      if (maxDateObj && selectedDate > maxDateObj) {
        return;
      }
    }

    if (selectedDate < minDateObj) {
      return;
    }

    const formattedDate = formatDate(selectedDate);

    if (selectingStart) {
      // When selecting start date
      setLocalStartDate(formattedDate);
      setLocalEndDate('');
      selectedStartRef.current = formattedDate;
      onChange(formattedDate, '');
      setSelectingStart(false);
    } else {
      // When selecting end date
      const startDate = selectedStartRef.current;
      if (startDate && selectedDate < parseDate(startDate)) {
        // If selected end date is before start date, swap them
        setLocalStartDate(formattedDate);
        setLocalEndDate(startDate);
        selectedStartRef.current = formattedDate;
        onChange(formattedDate, startDate);
      } else {
        // Normal end date selection
        setLocalEndDate(formattedDate);
        onChange(startDate, formattedDate);
      }
      setIsOpen(false);
      setSelectingStart(true);
    }

    setHoverDate(null);
  }

  function handlePrevMonth() {
    setViewDate(new Date(viewDate.getFullYear(), viewDate.getMonth() - 1));
  }

  function handleNextMonth() {
    setViewDate(new Date(viewDate.getFullYear(), viewDate.getMonth() + 1));
  }

  function isInRange(date) {
    if (!selectedStartDate) return false;

    const end = hoverDate || selectedEndDate;
    if (!end) return false;

    return date >= selectedStartDate && date <= end;
  }

  function isRangeStart(date) {
    return (
      selectedStartDate && formatDate(date) === formatDate(selectedStartDate)
    );
  }

  function isRangeEnd(date) {
    const end = selectedEndDate || hoverDate;
    return end && formatDate(date) === formatDate(end);
  }

  function handlePresetSelect(preset) {
    const { fromDate, toDate } = calculatePresetRange(preset);
    onChange(fromDate, toDate);
    setSelectingStart(true);
    setHoverDate(null);
    setIsOpen(false);
  }

  return (
    <div className="inline-flex sm:flex flex-col w-full sm:w-fit" ref={dropdownRef}>
      {label && (
        <label className="py-0.5 label">
          <span className="text-xs text-gray-600 font-[600] label-text">
            {label}
          </span>
        </label>
      )}
      <div className="relative w-full sm:w-fit">
        <button
          type="button"
          onClick={() => setIsOpen(!isOpen)}
          className="flex w-full sm:w-fit justify-between items-center bg-base-100 px-3 py-5 border border-gray-400 hover:border-base-content/40 focus:border-base-content/40 rounded-md focus:outline-none min-w-[210px] h-9 text-sm transition-colors duration-200"
        >
          <CalendarIcon className="sm:mr-2 ml-1 w-4 h-4 text-base-content/50" />
          <span>
            {startDate && endDate
              ? `${formatPanelDate(startDate)} - ${formatPanelDate(endDate)}`
              : startDate
                ? `${formatPanelDate(startDate)} - Select end date`
                : 'Select date range'}
          </span>
          <ChevronDownIcon className="mr-1 sm:ml-4 w-4 h-4 text-base-content/50" />
        </button>

        {isOpen && (
          <div className="z-50 absolute flex-col bg-base-100 shadow-lg mt-1.5 border border-base-content/20 rounded-b-xl">
            <div className="flex flex-row justify-between gap-4">
              {/* Left Calendar Panel */}
              <div className="px-4 pt-4 w-[320px]">
                {/* Calendar Header */}
                <div className="flex justify-between items-center mb-6">
                  <button
                    onClick={handlePrevMonth}
                    className="p-0 w-8 h-8 min-h-0 btn btn-ghost btn-sm"
                  >
                    <ChevronLeftIcon className="w-5 h-5 text-blue-800" />
                  </button>
                  <span className="px-2 font-medium text-[#314F5A] text-base">
                    {MONTHS[prevMonthDate.getMonth()]}{' '}
                    {prevMonthDate.getFullYear()}
                  </span>
                  <button className="invisible p-0 w-8 h-8 min-h-0 btn btn-ghost btn-sm"></button>
                </div>

                {/* Calendar Grid */}
                <div className="grid grid-cols-7">
                  {/* Day Headers */}
                  {DAYS.map((day, index) => (
                    <div
                      key={index}
                      className="bg-base-100 py-2 font-medium text-xs text-base-content/60 text-center"
                    >
                      {day}
                    </div>
                  ))}

                  {/* Calendar Days */}
                  {leftCalendarDays.map(({ date, isCurrentMonth }, index) => {
                    if (!isCurrentMonth) {
                      return <div key={index} className="w-full h-10" />;
                    }
                    const isStart = isRangeStart(date);
                    const isEnd = isRangeEnd(date);
                    const inRange = isInRange(date);
                    const isDisabled =
                      (maxDateObj && date > maxDateObj) || date < minDateObj;
                    const isToday = formatDate(date) === formatDate(new Date());
                    return (
                      <div
                        key={index}
                        className="relative flex justify-center items-center my-0.5 w-full h-10"
                      >
                        {/* Background range highlight */}
                        {inRange && (
                          <div
                            className={`absolute h-full w-full bg-[#F0F4F7] rounded-sm
                            ${isStart ? 'rounded-l-full' : ''}
                            ${isEnd ? 'rounded-r-full' : ''} z-0
                            `}
                          />
                        )}

                        {/* Foreground circle for start or end */}
                        {(isStart || isEnd) && (
                          <div className="z-10 absolute bg-[#019BBF] rounded-full w-full h-10" />
                        )}

                        {/* Date Text */}
                        <button
                          onClick={() => handleDateSelect(date)}
                          onMouseEnter={() =>
                            !selectingStart && setHoverDate(date)
                          }
                          onMouseLeave={() => setHoverDate(null)}
                          disabled={isDisabled}
                          className={`relative z-20 text-sm transition-colors duration-200
                          ${isCurrentMonth ? (isStart || isEnd ? 'text-white' : 'text-blue-900') : 'text-base-content/30'} font-medium
                          ${isDisabled ? 'cursor-not-allowed opacity-50 text-base-content/30' : ''}
                          `}
                        >
                          <span
                            className={`
                          flex items-center justify-center
                          ${isToday && !isStart && !isEnd
                                ? 'w-full h-10 rounded-full border border-1 border-blue-800'
                                : ''
                              }
                        `}
                          >
                            {date.getDate()}
                          </span>
                        </button>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/*Rignt Calendar Panel */}
              <div className="px-4 pt-4 w-[320px]">
                {/* Calendar Header */}
                <div className="flex justify-between items-center mb-6">
                  <button className="invisible p-0 w-8 h-8 min-h-0 btn btn-ghost btn-sm"></button>
                  <span className="px-2 font-medium text-[#314F5A] text-base">
                    {MONTHS[viewDate.getMonth()]} {viewDate.getFullYear()}
                  </span>
                  <button
                    onClick={handleNextMonth}
                    className="p-0 w-8 h-8 min-h-0 btn btn-ghost btn-sm"
                  >
                    <ChevronRightIcon className="w-5 h-5 text-blue-800" />
                  </button>
                </div>

                {/* Selection Status */}
                {!(startDate && endDate) && (
                  <p className="mb-4 text-xs text-base-content/60 text-center">
                    {selectingStart ? 'Select start date' : 'Select end date'}
                  </p>
                )}

                {/* Calendar Grid */}
                <div className="grid grid-cols-7">
                  {/* Day Headers */}
                  {DAYS.map((day, index) => (
                    <div
                      key={index}
                      className="bg-base-100 py-2 font-medium text-xs text-base-content/60 text-center"
                    >
                      {day}
                    </div>
                  ))}

                  {/* Calendar Days */}
                  {calendarDays.map(({ date, isCurrentMonth }, index) => {
                    if (!isCurrentMonth) {
                      return <div key={index} className="w-full h-10" />;
                    }

                    const isStart = isRangeStart(date);
                    const isEnd = isRangeEnd(date);
                    const inRange = isInRange(date);
                    const isDisabled =
                      (maxDateObj && date > maxDateObj) || date < minDateObj;
                    return (
                      <div
                        key={index}
                        className="relative flex justify-center items-center my-0.5 w-full h-10"
                      >
                        {/* Background range highlight */}
                        {inRange && (
                          <div
                            className={`absolute h-10 w-full bg-[#F0F4F7] rounded-sm
                            ${isStart ? 'rounded-l-full' : ''}
                            ${isEnd ? 'rounded-r-full' : ''} z-0
                            `}
                          />
                        )}

                        {/* Foreground circle for start or end */}
                        {(isStart || isEnd) && (
                          <div className="z-10 absolute bg-[#019BBF] rounded-full w-full h-10" />
                        )}

                        {/* Date Text */}
                        <button
                          onClick={() => handleDateSelect(date)}
                          onMouseEnter={() =>
                            !selectingStart && setHoverDate(date)
                          }
                          onMouseLeave={() => setHoverDate(null)}
                          disabled={isDisabled}
                          className={`relative z-20 text-sm transition-colors duration-200
                          ${isCurrentMonth ? (isStart || isEnd ? 'text-white' : 'text-blue-900') : 'text-base-content/30'} font-medium
                          ${isDisabled ? 'cursor-not-allowed opacity-50 text-base-content/30' : ''}
                          `}
                        >
                          {date.getDate()}
                        </button>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>

            <div className="flex justify-center w-full">
              <div className="flex justify-center items-center gap-2 py-5 border-t border-base-content/10 w-[85%]">
                <div className="px-3 py-1.5 text-gray-600">Quick Select</div>

                {PRESETS.map((preset) => (
                  <button
                    key={preset.label}
                    className="hover:bg-[#019BBF] px-3 py-1.5 border rounded-full font-[500] hover:text-white text-sm transition-all duration-300 ease-out"
                    onClick={() => handlePresetSelect(preset)}
                  >
                    {preset.label}
                  </button>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
